import { useEffect, useRef, useState } from "react";
import { Button, Modal, Space, Typography, message } from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

/**
 * Photographing an attachment instead of picking a file.
 *
 * Two mechanisms, because a phone and a desktop do not have the same camera:
 *
 * * **Touch devices use the operating system's camera app** (`<input capture>`).
 *   It opens on the first tap with no intermediate screen, and it brings
 *   autofocus, flash, zoom and HDR — a live `getUserMedia` preview reimplements
 *   none of that and photographs a dim valve pit visibly worse.
 * * **Desktops use `getUserMedia`**, because desktop browsers ignore `capture`
 *   entirely: without this branch the office half of the product would have no
 *   camera at all.
 *
 * Every captured photo is downscaled before it leaves the device — see
 * `downscalePhoto`.
 */

/** Is there any camera path at all on this device? */
export const cameraSupported = (): boolean => {
  if (typeof window === "undefined") return false;
  if (usesNativeCamera()) return true;
  return !!navigator.mediaDevices?.getUserMedia;
};

/**
 * A coarse pointer means a phone or a tablet, i.e. a device whose camera app is
 * one tap away and better than anything we can draw. Feature-detecting the
 * `capture` attribute is not possible — every browser accepts the property and
 * desktop ones simply ignore it.
 */
export const usesNativeCamera = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(pointer: coarse)").matches;

/**
 * Shrink a photograph to something a permit can carry over a site connection.
 *
 * A current phone shoots 12 MP — around 4000px and 3–6 MB. What the permit
 * needs is a legible photograph of a valve, a tag or a hazard, and the app
 * uploading it is often on one bar of rural signal. Scaling the long edge to
 * `maxDimension` at JPEG q0.82 turns 4 MB into roughly 300 KB.
 *
 * It also normalises EXIF orientation: `createImageBitmap` with
 * `imageOrientation: 'from-image'` bakes the rotation into the pixels, so a
 * portrait photo does not arrive sideways in the generated document (docxtpl
 * reads pixels, not EXIF).
 *
 * Failure is not fatal — a photo that cannot be re-encoded is uploaded as it
 * came off the camera. A slow upload beats a lost one.
 */
export const downscalePhoto = async (
  file: File,
  maxDimension: number
): Promise<File> => {
  if (!file.type.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file, {
      // Cast: this TypeScript lib predates `from-image` being added to
      // ImageOrientation. Browsers have supported it since 2021, and an older
      // one simply ignores an option it does not know.
      imageOrientation: "from-image",
    } as unknown as ImageBitmapOptions);
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > maxDimension ? maxDimension / longest : 1;
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.82)
    );
    if (!blob) return file;
    // Never hand back something larger than what arrived: a small photo
    // re-encoded can grow, and there is no point paying for that.
    if (blob.size >= file.size && scale === 1) return file;
    return new File([blob], photoFileName(file), { type: "image/jpeg" });
  } catch {
    return file;
  }
};

/** `IMG_0042.HEIC` off an iPhone becomes `IMG_0042.jpg` once re-encoded. */
const photoFileName = (file: File): string => {
  const base = (file.name || "photo").replace(/\.[^.]+$/, "");
  return `${base || "photo"}.jpg`;
};

/** A name for a shot taken through the webcam, which has no filename at all. */
export const timestampedPhotoName = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `photo-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.jpg`
  );
};

type CameraModalProps = {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
};

/**
 * The desktop camera: a live webcam preview, a shutter, and a review step.
 *
 * The review step is not decoration. A webcam shot is the one path where the
 * person cannot see the result before it is attached — the OS camera app on a
 * phone already asks "use photo / retake" — and an out-of-focus photograph of a
 * lockout tag attached to a permit is worse than no photograph.
 */
const CameraModal = ({ open, onClose, onCapture }: CameraModalProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string>("");
  const [shot, setShot] = useState<{ file: File; url: string } | null>(null);

  // Stop the camera whenever the modal is not showing it. A webcam light left
  // on after the dialog closed reads as the app spying, and on a laptop it is
  // the kind of thing that gets a tool banned from site.
  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setReady(false);
  };

  useEffect(() => {
    if (!open) {
      stopStream();
      return;
    }
    let cancelled = false;
    (async () => {
      setError("");
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // `environment` is a preference, not a demand: a laptop has only a
          // front camera and an exact constraint would fail outright there.
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch (e: any) {
        // Denied, already in use, or no camera. Say which — "camera failed"
        // sends people to IT for a permission they can grant themselves.
        setError(
          e?.name === "NotAllowedError"
            ? t("Camera access was blocked. Allow it in the browser's address bar and try again.")
            : e?.name === "NotFoundError"
            ? t("No camera was found on this device.")
            : t("The camera could not be started.")
        );
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Revoke the preview's object URL when it is replaced or the modal closes;
  // a retaken photo would otherwise leak its predecessor for the session.
  useEffect(() => {
    return () => {
      if (shot) URL.revokeObjectURL(shot.url);
    };
  }, [shot]);

  const takeShot = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) return;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], timestampedPhotoName(), {
          type: "image/jpeg",
        });
        setShot({ file, url: URL.createObjectURL(file) });
        stopStream();
      },
      "image/jpeg",
      0.92 // The review copy; downscalePhoto re-encodes once, on the way out.
    );
  };

  const close = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
    stopStream();
    onClose();
  };

  const use = () => {
    if (!shot) return;
    onCapture(shot.file);
    URL.revokeObjectURL(shot.url);
    setShot(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={close}
      title={t("Take photo")}
      width={720}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={close}>{t("Cancel")}</Button>
          {shot ? (
            <>
              <Button onClick={() => setShot(null)}>{t("Retake")}</Button>
              <Button type="primary" onClick={use}>
                {t("Use photo")}
              </Button>
            </>
          ) : (
            <Button type="primary" disabled={!ready} onClick={takeShot}>
              {t("Capture")}
            </Button>
          )}
        </Space>
      }
    >
      {error ? (
        <Typography.Text type="danger">{error}</Typography.Text>
      ) : shot ? (
        <img
          src={shot.url}
          alt={t("Captured photo")}
          style={{ width: "100%", borderRadius: 8, display: "block" }}
        />
      ) : (
        <video
          ref={videoRef}
          // `playsInline` keeps iOS from taking the preview fullscreen, and
          // `muted` is what lets autoplay start without a second gesture.
          playsInline
          muted
          style={{
            width: "100%",
            borderRadius: 8,
            display: "block",
            background: "#000",
            aspectRatio: "16 / 9",
            objectFit: "cover",
          }}
        />
      )}
    </Modal>
  );
};

type Props = {
  /** Longest edge a captured photo is scaled to, from the server's settings. */
  maxDimension: number;
  /** Handed a downscaled, ready-to-upload JPEG. */
  onCapture: (file: File) => void;
  disabled?: boolean;
  /** Rendered when several photos in a row are expected (multiple uploads). */
  multiple?: boolean;
};

/**
 * The "Take photo" button itself. Sits *beside* the ordinary upload button
 * rather than replacing it: `capture` on the only file input would take away
 * attaching a photo taken earlier, or a PDF, which is most of what attachments
 * are used for today.
 */
const PhotoCapture = ({ maxDimension, onCapture, disabled, multiple }: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [working, setWorking] = useState(false);

  const handleFile = async (file: File) => {
    setWorking(true);
    try {
      onCapture(await downscalePhoto(file, maxDimension));
    } catch {
      message.error(t("The photo could not be processed."));
    } finally {
      setWorking(false);
    }
  };

  const click = () => {
    if (usesNativeCamera()) inputRef.current?.click();
    else setModalOpen(true);
  };

  return (
    <>
      <Button
        icon={<CameraOutlined />}
        onClick={click}
        disabled={disabled}
        loading={working}
      >
        {t("Take photo")}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        // The whole point: on a phone this opens the camera directly instead of
        // the file browser. Desktop browsers ignore it, which is why they get
        // the modal above instead.
        capture="environment"
        multiple={multiple}
        style={{ display: "none" }}
        onChange={async (event) => {
          const files = Array.from(event.target.files ?? []);
          // Clear first: picking the same file twice in a row fires no change
          // event otherwise, and a retake of an identical shot looks ignored.
          event.target.value = "";
          for (const file of files) await handleFile(file);
        }}
      />
      <CameraModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCapture={handleFile}
      />
    </>
  );
};

export default PhotoCapture;
